import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  Grid,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken } from '../../../utils/authUtils';
import { API_PATHS } from '../../../utils/constants';

// Status chip component
const StatusChip = ({ status }) => {
  let color = 'default';
  
  switch (status) {
    case 'CREATED':
      color = 'info';
      break;
    case 'IN_PROGRESS':
      color = 'warning';
      break;
    case 'COMPLETED':
      color = 'success';
      break;
    case 'CANCELLED':
      color = 'error';
      break;
    default:
      color = 'default';
  }
  
  return <Chip label={status} color={color} size="small" />;
};

const WorkflowInstanceList = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [userOrganization, setUserOrganization] = useState(null);

  // Fetch user information to get organization
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          console.error('No auth token found');
          return;
        }

        const response = await axios.get(`${API_PATHS.API}/user/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.organisation) {
          setUserOrganization(response.data.organisation);
          console.log('User organization:', response.data.organisation);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        setError('Failed to fetch user information');
      }
    };

    fetchUserInfo();
  }, []);

  // Fetch workflow instances with caching to prevent continuous API calls
  useEffect(() => {
    const fetchInstances = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = getAuthToken();
        if (!token) {
          setError('Authentication token not found. Please log in again.');
          setLoading(false);
          return;
        }

        // Check if we have cached data in sessionStorage
        const cachedData = sessionStorage.getItem('workflowInstances');
        const cachedTimestamp = sessionStorage.getItem('workflowInstancesTimestamp');
        const currentTime = new Date().getTime();
        
        // Use cached data if it exists and is less than 5 minutes old
        if (cachedData && cachedTimestamp && (currentTime - parseInt(cachedTimestamp)) < 5 * 60 * 1000) {
          console.log('Using cached workflow instances data');
          const parsedData = JSON.parse(cachedData);
          
          // Filter by organization if needed
          if (userOrganization && userOrganization.type === 'CUSTOMER') {
            const filteredData = parsedData.filter(instance => 
              instance.customerOrganisationId === userOrganization.id
            );
            setInstances(filteredData);
            console.log(`Using cached data: ${filteredData.length} instances for organization ${userOrganization.name}`);
          } else {
            setInstances(parsedData);
            console.log(`Using cached data: ${parsedData.length} instances`);
          }
          
          setLoading(false);
          return;
        }

        // If no valid cache, fetch from API
        // Using the correct workflow instances endpoint path
        const response = await axios.get(`${API_PATHS.API}/workflow-instances`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data) {
          // Cache the response data
          sessionStorage.setItem('workflowInstances', JSON.stringify(response.data));
          sessionStorage.setItem('workflowInstancesTimestamp', currentTime.toString());
          
          // If user is a customer admin/employee, filter instances by their organization
          if (userOrganization && userOrganization.type === 'CUSTOMER') {
            const filteredData = response.data.filter(instance => 
              instance.customerOrganisationId === userOrganization.id
            );
            setInstances(filteredData);
            console.log(`Fetched and filtered ${filteredData.length} instances for organization ${userOrganization.name}`);
          } else {
            // For admin users, show all instances
            setInstances(response.data);
            console.log(`Fetched ${response.data.length} instances`);
          }
        }
      } catch (error) {
        console.error('Error fetching workflow instances:', error);
        setError('Failed to fetch workflow instances. ' + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    };

    // Only fetch instances if we have user organization info or if we're not a customer user
    if (userOrganization) {
      fetchInstances();
    }
  }, [userOrganization]);

  const handleViewInstance = (instance) => {
    navigate(`/admin/workflow/instance/${instance.id}`, { 
      state: { 
        requestId: instance.formattedRequestId || 
          (instance.organizationRequestPrefix && instance.requestNumber ? 
            `${instance.organizationRequestPrefix}-R-${instance.requestNumber}` : 
            instance.id.substring(0, 8))
      } 
    });
  };

  const filteredInstances = instances.filter(instance => {
    const searchTermLower = searchTerm.toLowerCase();
    const formattedId = instance.formattedRequestId || 
      (instance.organizationRequestPrefix && instance.requestNumber ? 
        `${instance.organizationRequestPrefix}-R-${instance.requestNumber}` : 
        instance.id.substring(0, 8));
    
    return instance.workflowName.toLowerCase().includes(searchTermLower) ||
      instance.customerName?.toLowerCase().includes(searchTermLower) ||
      instance.status.toLowerCase().includes(searchTermLower) ||
      formattedId.toLowerCase().includes(searchTermLower);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Workflow Instances
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {userOrganization && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Showing workflow requests for: {userOrganization.name} ({userOrganization.organizationCode || 'No code'})
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Search by ID, name, customer, or status..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <IconButton>
            <FilterIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {filteredInstances.length === 0 ? (
              <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
                No workflow instances found.
              </Typography>
            ) : (
              filteredInstances.map((instance) => (
                <Paper
                  key={instance.id}
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 2,
                    '&:hover': {
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Box>
                          <Typography variant="h6" component="h2">
                            {instance.workflowName}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Chip 
                              label={instance.formattedRequestId || 
                                (instance.organizationRequestPrefix && instance.requestNumber ? 
                                  `${instance.organizationRequestPrefix}-R-${instance.requestNumber}` : 
                                  instance.id.substring(0, 8))}
                              color="primary"
                              size="small"
                              onClick={() => handleViewInstance(instance)}
                              sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <StatusChip status={instance.status} />
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                          Customer: {instance.customerName}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          Created: {new Date(instance.createDateTime).toLocaleString()}
                        </Typography>
                        {instance.completedDateTime && (
                          <Typography variant="body2" color="textSecondary">
                            Completed: {new Date(instance.completedDateTime).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewInstance(instance)}
                        title="View Instance"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Tickets
                  </Typography>
                  <Grid container spacing={1}>
                    {instance.tickets.map((ticket) => (
                      <Grid item key={ticket.id} xs={12} sm={6} md={4} lg={3}>
                        <Box
                          sx={{
                            border: '1px solid #eee',
                            borderRadius: 1,
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                          }}
                        >
                          <Typography variant="body2" noWrap>
                            {ticket.stepName}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                              {ticket.ticketNumber}
                            </Typography>
                            <StatusChip status={ticket.status} />
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default WorkflowInstanceList;
